import { API_BASE_PATH } from '../api/constants.js';

const ENDPOINT = `${API_BASE_PATH}/frontend-logs`;
const MAX_QUEUE_SIZE = 50;
const MAX_ARGS = 6;
const MAX_TEXT_LENGTH = 1000;
const FLUSH_DELAY_MS = 1000;
const REDACTED = '[redacted]';
const SENSITIVE_KEYS = new Set([
  'authorization',
  'access_token',
  'refresh_token',
  'csrf_token',
  'csrf',
  'x-csrf-token',
  'cookie',
  'set-cookie',
  'password',
  'senha',
  'token',
  'secret',
]);

let installed = false;
let queue = [];
let flushTimer = null;
let flushing = false;

const truncateText = (value, maxLength = MAX_TEXT_LENGTH) => {
  const text = String(value ?? '');
  const compact = text.replace(/\s+/g, ' ').trim();
  return compact.length > maxLength ? `${compact.slice(0, maxLength)}...` : compact;
};

const redactText = (value) => {
  let output = value;
  output = output.replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, `Bearer ${REDACTED}`);
  output = output.replace(/(access_token|refresh_token|csrf_token)=([^;\s]+)/gi, `$1=${REDACTED}`);
  return output;
};

const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  try {
    const entries = document.cookie ? document.cookie.split(';') : [];
    for (const entry of entries) {
      const [key, ...rest] = entry.trim().split('=');
      if (key === name) {
        return decodeURIComponent(rest.join('='));
      }
    }
  } catch {
    return null;
  }
  return null;
};

const serializeValue = (value, depth = 0) => {
  if (depth > 3) return '[max-depth]';
  if (value === null || value === undefined) return value ?? null;
  if (typeof value === 'string') return truncateText(redactText(value));
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Error) {
    return {
      name: truncateText(value.name || 'Error', 120),
      message: truncateText(redactText(value.message || ''), 500),
      stack: truncateText(redactText(value.stack || ''), 1200),
    };
  }
  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARGS).map((item) => serializeValue(item, depth + 1));
  }
  if (typeof value === 'object') {
    const result = {};
    let count = 0;
    for (const [key, item] of Object.entries(value)) {
      if (count >= 24) {
        result['...'] = 'truncated';
        break;
      }
      count += 1;
      const safeKey = truncateText(key, 80);
      result[safeKey] = SENSITIVE_KEYS.has(safeKey.trim().toLowerCase())
        ? REDACTED
        : serializeValue(item, depth + 1);
    }
    return result;
  }
  return truncateText(redactText(String(value)));
};

const buildMessage = (args) => {
  const first = args[0];
  if (typeof first === 'string') return truncateText(redactText(first), 500);
  if (first instanceof Error) return truncateText(redactText(first.message || first.name), 500);
  return truncateText(redactText(JSON.stringify(serializeValue(first))), 500);
};

const buildStack = (args) => {
  for (const arg of args) {
    if (arg instanceof Error && arg.stack) {
      return truncateText(redactText(arg.stack), 1200);
    }
  }
  return null;
};

const scheduleFlush = () => {
  if (flushTimer || flushing) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushFrontendLogs();
  }, FLUSH_DELAY_MS);
};

const enqueueFrontendLog = (entry) => {
  if (typeof window === 'undefined') return;
  queue.push(entry);
  if (queue.length > MAX_QUEUE_SIZE) {
    queue = queue.slice(queue.length - MAX_QUEUE_SIZE);
  }
  scheduleFlush();
};

export const flushFrontendLogs = async () => {
  if (typeof fetch !== 'function' || flushing || queue.length === 0) return;

  const logs = queue.splice(0, 20);
  const csrf = readCookie('csrf_token');
  const headers = {
    'Content-Type': 'application/json',
  };
  if (csrf) {
    headers['X-CSRF-Token'] = csrf;
  }

  flushing = true;
  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      headers,
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify({ logs }),
    });
  } catch {
    queue = logs.concat(queue).slice(0, MAX_QUEUE_SIZE);
  } finally {
    flushing = false;
    if (queue.length > 0) scheduleFlush();
  }
};

const captureConsole = (level, originalMethod) => (...args) => {
  originalMethod(...args);
  enqueueFrontendLog({
    level,
    message: buildMessage(args),
    source: 'front.console',
    url: window.location?.href || null,
    user_agent: navigator.userAgent || null,
    stack: buildStack(args),
    args: args.slice(0, MAX_ARGS).map((arg) => serializeValue(arg)),
    meta: {
      build_id: import.meta.env.VITE_APP_BUILD_ID || import.meta.env.VITE_COMMIT_SHA || null,
    },
  });
};

export const installFrontendLogger = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalError = console.error.bind(console);
  const originalWarn = console.warn.bind(console);
  console.error = captureConsole('error', originalError);
  console.warn = captureConsole('warning', originalWarn);

  window.addEventListener('error', (event) => {
    enqueueFrontendLog({
      level: 'error',
      message: truncateText(redactText(event.message || 'Uncaught error'), 500),
      source: 'front.window.error',
      url: window.location?.href || null,
      user_agent: navigator.userAgent || null,
      stack: event.error?.stack ? truncateText(redactText(event.error.stack), 1200) : null,
      args: [],
      meta: {
        filename: event.filename || null,
        lineno: event.lineno || null,
        colno: event.colno || null,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    enqueueFrontendLog({
      level: 'error',
      message:
        reason instanceof Error
          ? truncateText(redactText(reason.message || reason.name), 500)
          : truncateText(redactText(String(reason || 'Unhandled promise rejection')), 500),
      source: 'front.unhandledrejection',
      url: window.location?.href || null,
      user_agent: navigator.userAgent || null,
      stack: reason instanceof Error && reason.stack ? truncateText(redactText(reason.stack), 1200) : null,
      args: [serializeValue(reason)],
      meta: {},
    });
  });

  window.addEventListener('pagehide', () => {
    void flushFrontendLogs();
  });
};
